/**
 * The error shape every AWJ endpoint returns.
 *
 * The API answers failures with RFC 9457 ProblemDetails and a stable machine-readable `code`,
 * so the client branches on the code rather than on a status number or a message string. Status
 * codes are too coarse — three different 409s mean three different things — and messages are
 * copy that changes.
 */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  /** e.g. `cart.insufficient_stock`. The thing to branch on. */
  code?: string;
  /** Present on a 400 from validation: field name to messages. */
  errors?: Record<string, string[]>;
  /** Echoed on every error, and worth showing to a customer reporting a problem. */
  correlationId?: string;
}

/**
 * A failed request. Carries the parsed problem document so a caller can react to the specific
 * failure rather than to "something went wrong".
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: ProblemDetails,
  ) {
    super(problem.detail ?? problem.title ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
  }

  /** The stable identifier, e.g. `auth.invalid_credentials`. */
  get code(): string | undefined {
    return this.problem.code;
  }

  get correlationId(): string | undefined {
    return this.problem.correlationId;
  }

  /** Field-level messages, ready to attach to a form. */
  get fieldErrors(): Record<string, string[]> {
    return this.problem.errors ?? {};
  }

  /** The first message for a field, which is what a single-line form error shows. */
  fieldError(field: string): string | undefined {
    return this.problem.errors?.[field]?.[0];
  }

  is(code: string): boolean {
    return this.problem.code === code;
  }

  /** True when signing in again would plausibly fix it. */
  get isUnauthorised(): boolean {
    return this.status === 401;
  }

  get isValidation(): boolean {
    return this.status === 400;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

/** A transport failure — offline, DNS, a dropped connection. Never a response from the API. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('The request could not be sent. Check your connection and try again.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}
